import { isRecord } from '@/utils/objects';
import type { GroupNode, ConditionNode } from '../types';
import { packForeignEvents } from './parse-wait-until';

type ParseFunction = (raw: Record<string, unknown>) => ConditionNode;

function tryParseGroup(
  source: Record<string, unknown>,
  key: 'and' | 'or',
  parseChild: ParseFunction,
): GroupNode | null {
  const value = source[key];
  if (value == null) return null;

  let items: unknown[];
  if (Array.isArray(value)) {
    items = value;
  } else if (typeof value === 'object' && 'children' in value) {
    const children = value.children;
    if (!Array.isArray(children)) return null;
    items = children;
  } else {
    return null;
  }

  const children = packForeignEvents(items).filter(isRecord).map(parseChild);

  return { type: 'group', operator: key, children };
}

/**
 * Parse group conditions: { and: [...] }, { or: [...] }, { and: { children: [...] } }.
 * Returns null if the object is not a group. A `{ not }` wrapper is read by
 * `negateCondition` before this runs.
 */
export function parseGroup(raw: Record<string, unknown>, parseChild: ParseFunction): GroupNode | null {
  // Prefer whichever key has actual content
  const andResult = tryParseGroup(raw, 'and', parseChild);
  const orResult = tryParseGroup(raw, 'or', parseChild);

  if (andResult != null && andResult.children.length > 0) return andResult;
  if (orResult != null && orResult.children.length > 0) return orResult;
  return andResult ?? orResult;
}
